// frontend/src/components/Sidebar.js

import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Avatar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Assignment as ProjectsIcon,
  Settings as SettingsIcon,
  ChevronLeft as ChevronLeftIcon,
  Menu as MenuIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import logo from '../images/zaytoon_logo_black.png';

const drawerWidth = 240;
const collapsedWidth = 65;

function Sidebar({ open, onDrawerToggle, userEmail, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    localStorage.removeItem('token');
    localStorage.removeItem('userEmail');
    onLogout();
    navigate('/login');
  };

  const menuItems = [
    { text: 'Projects', icon: <ProjectsIcon />, path: '/dashboard/projects' },
  ];

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: open ? drawerWidth : collapsedWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: open ? drawerWidth : collapsedWidth,
          boxSizing: 'border-box',
          bgcolor: 'background.paper',
          borderRight: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 0 20px rgba(0,0,0,0.03)',
          overflowX: 'hidden',
          transition: 'width 0.3s ease',
        },
      }}
    >
      <Box sx={{ 
        p: 2, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: open ? 'space-between' : 'center',
        minHeight: '64px'
      }}>
        {open ? (
          <Box sx={{ display: 'flex', alignItems: 'center', pl: 1 }}>
            <img src={logo} alt="Zaytoon Logo" style={{ height: 32 }} />
          </Box>
        ) : (
          <IconButton 
            onClick={onDrawerToggle}
            sx={{ 
              color: 'primary.main',
              padding: '8px',
              '&:hover': { 
                bgcolor: 'primary.lighter'
              } 
            }}
          >
            <MenuIcon />
          </IconButton>
        )}
        {open && (
          <IconButton 
            onClick={onDrawerToggle}
            sx={{ 
              color: 'primary.main',
              padding: '8px',
              '&:hover': { 
                bgcolor: 'primary.lighter'
              } 
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
        )}
      </Box>

      <List sx={{ px: 1, py: 2, flexGrow: 1 }}>
        {menuItems.map((item) => (
          <ListItem
            button
            key={item.text}
            onClick={() => navigate(item.path)}
            selected={location.pathname.includes(item.path)}
            sx={{
              borderRadius: 1.5,
              mb: 0.5,
              cursor: 'pointer',
              justifyContent: open ? 'flex-start' : 'center',
              px: 2,
              minHeight: '44px',
              '&.Mui-selected': {
                bgcolor: 'primary.lighter',
                color: 'primary.main',
                '&:hover': {
                  bgcolor: 'primary.lighter',
                },
                '& .MuiListItemIcon-root': {
                  color: 'primary.main',
                },
              },
              '&:hover': {
                bgcolor: 'grey.50',
              },
            }}
          >
            <ListItemIcon 
              sx={{ 
                color: 'grey.600', 
                minWidth: open ? 40 : 0,
                mr: open ? 2 : 0,
                justifyContent: open ? 'flex-start' : 'center'
              }}
            >
              {item.icon}
            </ListItemIcon>
            {open && (
              <ListItemText 
                primary={item.text} 
                primaryTypographyProps={{ 
                  fontSize: '0.875rem',
                  fontWeight: location.pathname.includes(item.path) ? 600 : 400
                }}
              />
            )}
          </ListItem>
        ))}
      </List>

      <List sx={{ px: 1, py: 1 }}>
        <ListItem
          button
          onClick={() => navigate('/dashboard/settings')}
          selected={location.pathname.includes('/dashboard/settings')}
          sx={{
            borderRadius: 1.5,
            cursor: 'pointer',
            justifyContent: open ? 'flex-start' : 'center',
            px: 2,
            minHeight: '44px',
            '&.Mui-selected': {
              bgcolor: 'primary.lighter',
              color: 'primary.main',
              '& .MuiListItemIcon-root': {
                color: 'primary.main',
              },
            },
            '&:hover': {
              bgcolor: 'grey.50',
            },
          }}
        >
          <ListItemIcon 
            sx={{ 
              color: 'grey.600', 
              minWidth: open ? 40 : 0,
              mr: open ? 2 : 0,
              justifyContent: open ? 'flex-start' : 'center'
            }}
          >
            <SettingsIcon />
          </ListItemIcon>
          {open && (
            <ListItemText 
              primary="Settings" 
              primaryTypographyProps={{ 
                fontSize: '0.875rem',
                fontWeight: location.pathname.includes('/dashboard/settings') ? 600 : 400
              }}
            />
          )}
        </ListItem>

        <Box sx={{ 
          p: 2, 
          mt: 1,
          display: 'flex',
          justifyContent: open ? 'flex-start' : 'center'
        }}>
          <Box 
            onClick={handleMenuClick}
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1.5,
              justifyContent: open ? 'flex-start' : 'center',
              width: '100%',
              cursor: 'pointer',
              '&:hover': {
                opacity: 0.8
              }
            }}
          >
            <Avatar 
              sx={{ 
                width: 32, 
                height: 32,
                bgcolor: 'primary.lighter',
                color: 'primary.main',
                fontSize: '0.875rem',
              }}
            >
              {userEmail ? userEmail[0].toUpperCase() : '?'}
            </Avatar>
            {open && (
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'text.primary',
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap'
                }}
              >
                {userEmail || 'Loading...'}
              </Typography>
            )}
          </Box>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
          >
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Logout</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </List>
    </Drawer>
  );
}

export default Sidebar;